import { Injectable } from "@angular/core";
import { BehaviorSubject, map, tap, Observable } from "rxjs";
import { HttpClient, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { LoaderService } from "./loader.service";
import { ActionResult, EventItem, WspayConfirmation, WspayForm, WspayFormRequest, WspayResult } from "@model";
import { EnvironmentService } from "./environment.service";

export const WSPAY_RESULT_URL = "/api/public/payment/wspay/result";

/// https://docs.wspay.info/wspayform
@Injectable({ providedIn: 'root' })
export class WsPayService {
  private static _currentWspayConfirmation = new BehaviorSubject<WspayConfirmation | undefined>(undefined);

  get currentWspayConfirmation() {
    return WsPayService._currentWspayConfirmation;
  }

  constructor(
    private loaderService: LoaderService,
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  fetchWsPayForm(wsFormRequest: WspayFormRequest, onSuccess: (redirectUrl: string) => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<WspayForm>>(this.environmentService.getApiUrl() + "/api/public/payment/wspay/form", wsFormRequest)
      .pipe(tap(() => this.loaderService.dismissLoading()))
      .subscribe(
        response => onSuccess(response.result?.PaymentFormUrl ?? ''),
        () => onError()
      );
  }

  updateWsPayResult(wsPayResult: WspayResult, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<WspayConfirmation>>(this.environmentService.getApiUrl() + WSPAY_RESULT_URL, wsPayResult)
      .pipe(tap(() => this.loaderService.dismissLoading()))
      .subscribe(
        response => this.currentWspayConfirmation.next(response.result),
        error => {
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
            onError();
          }
        }
      );
  }

  fetchWsPayResult(wsPayResult: WspayResult): Observable<WspayConfirmation | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<WspayConfirmation>>(this.environmentService.getApiUrl() + WSPAY_RESULT_URL, wsPayResult)
      .pipe(
        tap(() => this.loaderService.dismissLoading()),
        map(actionResult => {
          const result = actionResult.result;
          if (result) {
            result.event = {
              name: result.eventName,
              nameEng: result.eventNameEng,
              nameIta: result.eventNameIta,
              nameSlo: result.eventNameSlo,
              nameSr: result.eventNameSr,
              defaultLanguage: result.eventDefaultLang,
              start: result.eventStart
            } as EventItem;
          }
          return result ?? null;
        })
      );
  }
}
